import {z} from 'zod'

export const BugReportFormSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    description: z.string().optional().default('')
})

export type BugReportFormValues = z.infer<typeof BugReportFormSchema>

export type EnvironmentSnapshot = {
    reporterName: string
    reporterEmail: string
    url: string
    browser: string
    os: string
    screenResolution: string
    viewportSize: string
    appVersion: string
    timestamp: string
}

/**
 * A single attachment reference, as returned by the backend after a successful
 * upload and as sent inside the create-issue payload.
 */
export const BugReportAttachmentRef = z.object({
    assetUrl: z.string(),
    filename: z.string(),
    contentType: z.string()
})

export type BugReportAttachmentRef = z.infer<typeof BugReportAttachmentRef>

export type BugReportIssueCreatePayload = {
    title: string
    description: string
    environment: EnvironmentSnapshot
    attachments: BugReportAttachmentRef[]
}

export const CreatedBugReportIssue = z.object({
    id: z.string(),
    identifier: z.string(),
    title: z.string(),
    url: z.string()
})

export type CreatedBugReportIssue = z.infer<typeof CreatedBugReportIssue>

export const BugReportIssueStateType = z.union([
    z.literal('triage'),
    z.literal('backlog'),
    z.literal('unstarted'),
    z.literal('started'),
    z.literal('completed'),
    z.literal('canceled')
])

export type BugReportIssueStateType = z.infer<typeof BugReportIssueStateType>

export const BugReportIssueState = z.object({
    name: z.string(),
    color: z.string(),
    type: BugReportIssueStateType
})

export type BugReportIssueState = z.infer<typeof BugReportIssueState>

export const BugReportIssueAssignee = z.object({
    name: z.string(),
    avatarUrl: z.string().nullable()
})

export type BugReportIssueAssignee = z.infer<typeof BugReportIssueAssignee>

export const BugReportIssue = z.object({
    id: z.string(),
    identifier: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    url: z.string(),
    createdAt: z.string(),
    completedAt: z.string().nullish(),
    state: BugReportIssueState.nullable(),
    assignee: BugReportIssueAssignee.nullish(),
    commentCount: z.number()
})

export type BugReportIssue = z.infer<typeof BugReportIssue>

export const BugReportIssueList = z.array(BugReportIssue)

export type BugReportIssueList = z.infer<typeof BugReportIssueList>

/**
 * A single read-only comment on a bug-report issue, as returned by
 * `GET /bug-reporter/issues/:id/comments`.
 */
export const BugReportCommentAuthor = z.object({
    name: z.string(),
    avatarUrl: z.string().nullable()
})

export type BugReportCommentAuthor = z.infer<typeof BugReportCommentAuthor>

export const BugReportComment = z.object({
    id: z.string(),
    body: z.string(),
    createdAt: z.string(),
    user: BugReportCommentAuthor.nullable()
})

export type BugReportComment = z.infer<typeof BugReportComment>

export const BugReportCommentList = z.array(BugReportComment)

export type BugReportCommentList = z.infer<typeof BugReportCommentList>

/**
 * Client-local state for one attachment as it moves through the dropzone
 * lifecycle. Never sent over the wire — only the `done` entries are projected
 * into `BugReportAttachmentRef[]` at submit time.
 */
export type AttachmentUploadStatus = 'pending' | 'uploading' | 'done' | 'error'

export type AttachmentUploadState = {
    id: string
    file: File
    status: AttachmentUploadStatus
    assetUrl: string | null
    error: string | null
}
